/**
 * ProfileIconSelector Component
 *
 * Modal for selecting a profile icon from available options.
 * Shows circular cropped previews of each character.
 * Locked icons are shown with a lock overlay and unlock requirements.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles, Lock } from 'lucide-react';
import ProfileIcon, { ICON_CONFIG } from './ProfileIcon';
import { apiGetProfileIcons, apiUpdateProfileIcon } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const ProfileIconSelector = ({ currentIcon, onClose, onIconChange }) => {
  const { authToken } = useAuth();
  const [selectedIcon, setSelectedIcon] = useState(currentIcon || 'pikachu');
  const [allIcons, setAllIcons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showLockedTooltip, setShowLockedTooltip] = useState(null);

  useEffect(() => {
    const loadIcons = async () => {
      try {
        const result = await apiGetProfileIcons(authToken);
        if (result) {
          // New API returns allIcons with unlock status
          if (result.allIcons) {
            setAllIcons(result.allIcons);
          } else if (result.icons) {
            // Backwards compatibility - just unlocked icons array
            // Build allIcons from ICON_CONFIG
            setAllIcons(Object.keys(ICON_CONFIG).map(key => ({
              key,
              unlocked: result.icons.includes(key),
              unlockCondition: ICON_CONFIG[key]?.unlockDesc
                ? { desc: ICON_CONFIG[key].unlockDesc }
                : { desc: 'Always available' }
            })));
          } else if (Array.isArray(result)) {
            // Even older API - just array of keys
            setAllIcons(Object.keys(ICON_CONFIG).map(key => ({
              key,
              unlocked: result.includes(key),
              unlockCondition: ICON_CONFIG[key]?.unlockDesc
                ? { desc: ICON_CONFIG[key].unlockDesc }
                : { desc: 'Always available' }
            })));
          }
        }
      } catch (err) {
        console.error('Failed to load icons:', err);
        // Fallback to showing all icons from config
        setAllIcons(Object.keys(ICON_CONFIG).map(key => ({
          key,
          unlocked: !ICON_CONFIG[key]?.unlock, // Unlocked if no unlock condition
          unlockCondition: ICON_CONFIG[key]?.unlockDesc
            ? { desc: ICON_CONFIG[key].unlockDesc }
            : { desc: 'Always available' }
        })));
      }
    };
    loadIcons();
  }, [authToken]);

  const handleIconClick = (iconKey, isUnlocked) => {
    if (isUnlocked) {
      setSelectedIcon(iconKey);
      setShowLockedTooltip(null);
    } else {
      // Show tooltip for locked icon
      setShowLockedTooltip(iconKey);
    }
  };

  const handleSave = async () => {
    if (selectedIcon === currentIcon) {
      onClose();
      return;
    }

    // Check if selected icon is unlocked
    const iconData = allIcons.find(i => i.key === selectedIcon);
    if (iconData && !iconData.unlocked) {
      setError('This icon is locked. Unlock it first!');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const result = await apiUpdateProfileIcon(selectedIcon, authToken);

      if (result && !result.error) {
        onIconChange(selectedIcon);
        onClose();
      } else {
        setError(result?.error || 'Failed to save icon. Please try again.');
      }
    } catch (err) {
      console.error('Save icon error:', err);
      setError('Failed to save icon. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-pocket-blue to-pocket-red p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-white" />
              <h2 className="text-lg font-bold text-white">Choose Your Icon</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X size={20} className="text-white" />
            </button>
          </div>

          {/* Current Selection Preview */}
          <div className="p-6 bg-gradient-to-b from-pocket-bg to-white text-center">
            <div className="relative inline-block">
              <ProfileIcon
                icon={selectedIcon}
                size={96}
                showBorder={true}
                className="ring-4 ring-pocket-blue shadow-lg mx-auto"
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-pocket-blue text-white text-xs font-bold px-3 py-1 rounded-full">
                {ICON_CONFIG[selectedIcon]?.name || selectedIcon}
              </div>
            </div>
          </div>

          {/* Icon Grid */}
          <div className="p-4">
            <p className="text-sm text-pocket-text-light text-center mb-4">
              Select an icon to represent you in battles
            </p>
            <div className="grid grid-cols-3 gap-4">
              {allIcons.map((iconData) => {
                const config = ICON_CONFIG[iconData.key];
                if (!config) return null;

                const isSelected = selectedIcon === iconData.key;
                const isUnlocked = iconData.unlocked;
                const isShowingTooltip = showLockedTooltip === iconData.key;

                return (
                  <motion.button
                    key={iconData.key}
                    whileHover={{ scale: isUnlocked ? 1.05 : 1.02 }}
                    whileTap={{ scale: isUnlocked ? 0.95 : 1 }}
                    onClick={() => handleIconClick(iconData.key, isUnlocked)}
                    onBlur={() => setShowLockedTooltip(null)}
                    className={`relative p-3 rounded-xl transition-all ${
                      isSelected && isUnlocked
                        ? 'bg-pocket-blue/10 ring-2 ring-pocket-blue'
                        : isUnlocked
                        ? 'bg-pocket-bg hover:bg-gray-100'
                        : 'bg-gray-100 cursor-not-allowed'
                    }`}
                  >
                    <div className="relative">
                      <ProfileIcon
                        icon={iconData.key}
                        size={56}
                        showBorder={false}
                        className={`mx-auto mb-2 ${!isUnlocked ? 'opacity-40 grayscale' : ''}`}
                      />
                      {/* Lock overlay for locked icons */}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-8 h-8 bg-gray-700/80 rounded-full flex items-center justify-center">
                            <Lock size={16} className="text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <p
                      className={`font-medium text-center leading-tight ${
                        isSelected && isUnlocked ? 'text-pocket-blue' : isUnlocked ? 'text-pocket-text' : 'text-gray-400'
                      }`}
                      style={{ fontSize: config.name.length > 12 ? '0.6rem' : '0.75rem' }}
                    >
                      {config.name}
                    </p>

                    {/* Selection indicator */}
                    {isSelected && isUnlocked && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-pocket-blue rounded-full flex items-center justify-center"
                      >
                        <Check size={14} className="text-white" />
                      </motion.div>
                    )}

                    {/* Unlock requirement tooltip - positioned above to avoid overflow */}
                    {isShowingTooltip && !isUnlocked && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute z-10 left-0 right-0 -top-16 bg-gray-800 text-white text-xs p-2 rounded-lg shadow-lg whitespace-normal"
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <Lock size={10} />
                          <span className="font-bold">Locked</span>
                        </div>
                        <p className="break-words">{iconData.unlockCondition?.desc || config.unlockDesc || 'Complete requirement to unlock'}</p>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 bg-pocket-bg border-t border-gray-200">
            {error && (
              <p className="text-red-500 text-sm text-center mb-3">{error}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-pocket-text font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-pocket-blue text-white font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfileIconSelector;
